import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings } from 'lucide-react';
import { useUserSystems, useDefaultSystem, useUpdateDefaultSystem } from '@/hooks/useUserSystems';

interface DefaultSystemModalProps {
  trigger?: React.ReactNode;
}

export function DefaultSystemModal({ trigger }: DefaultSystemModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSystemId, setSelectedSystemId] = useState<string | null>(null);

  const { data: systems = [], isLoading: systemsLoading } = useUserSystems();
  const { data: defaultSystemId } = useDefaultSystem();
  const updateDefaultSystem = useUpdateDefaultSystem();

  // Initialize selected system when modal opens
  React.useEffect(() => {
    if (isOpen && defaultSystemId) {
      setSelectedSystemId(defaultSystemId);
    } else if (isOpen && !defaultSystemId) {
      setSelectedSystemId(null);
    }
  }, [isOpen, defaultSystemId]);

  const handleSave = async () => {
    try {
      await updateDefaultSystem.mutateAsync(selectedSystemId);
      setIsOpen(false);
    } catch (error) {
      // Error is handled by the mutation hook
    }
  };

  const handleClear = () => {
    setSelectedSystemId(null);
  };

  // Don't show modal if user has only one system (no choice needed)
  if (systems.length <= 1) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="sm" className="justify-start">
            <Settings className="mr-2 h-4 w-4" />
            Sistema Padrão
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Configurar Sistema Padrão</DialogTitle>
          <DialogDescription>
            Escolha qual sistema deve ser aberto automaticamente após o login.
            Se nenhum for selecionado, será usado o primeiro sistema disponível.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {systemsLoading ? (
            <div className="text-center py-4">Carregando sistemas...</div>
          ) : (
            <>
              <Select
                value={selectedSystemId || ''}
                onValueChange={setSelectedSystemId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um sistema padrão" />
                </SelectTrigger>
                <SelectContent>
                  {systems.map((system) => (
                    <SelectItem key={system.id} value={system.id}>
                      {system.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClear}
                  disabled={!selectedSystemId}
                >
                  Limpar Seleção
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={updateDefaultSystem.isPending}
                  className="ml-auto"
                >
                  {updateDefaultSystem.isPending ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}